import React, { useState } from "react";
import md5 from "js-md5";
import { v4 as uuid } from "uuid";
import {
  displayUserName,
  formatAddress,
  withApi,
  withIntl,
  withNotifier,
  withUser,
  apiShape,
  intlShape,
  notifierShape,
  userShape,
  ActionBar,
  ActionBarButtons,
  Container,
  Details,
  DetailsHeaderItem,
  DetailsPicture,
  EditActionButton,
  Heading,
  Loader,
  VatPrefixes,
  withFlipper,
  Row,
} from "lcm-iot-commons";
import isElementHiddenViaFlipper from "lcm-iot-commons/client/lib/utils/flipperUtils";
import DataExports from "lcm-iot-commons/client/lib/components/DataExports/DataExports";
import { FormattedMessage } from "react-intl";
import { Column } from "@netilion/react-components";
import UserPasswordEdit from "./UserPasswordEdit";
import MultiFactorAuthenticationDetails from "./MultiFactorAuthenticationDetails";
import MyEndressMfaDetails from "./MyEndressMfaDetails";
import DemoDataHandler from "./DemoDataHandler";
import NewsletterHandler from "./NewsletterHandler";
import LoginDetails from "./LoginDetails";
import NotificationEmailsHandler from "./NotificationEmailsHandler";
import { getTimeZone } from "../../utils/timeZones";
import { getLocaleFormat, getLocaleOption } from "../../utils/locales";
import UserProfileCustomersHelp from "./UserProfileCustomersHelp";

type Props = {
  api: apiShape;
  intl: intlShape;
  notifier: notifierShape;
  user: userShape;
  flipper: {
    demoDataActive?: boolean;
    hideUserProfileNotificationSettings?: boolean;
    isVerifyActive?: boolean;
  };
};

type User = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string;
  address: {
    companyName: string;
    streetName: string;
    streetNumber: string;
    city: string;
    zipCode: string;
    regionCode: string;
    countryName: string;
    countryCode: string;
    vatNumber: string;
    billingEmail: string;
  };
  phone: string;
  time_zone: string;
  locale: string;
  sso_provider: string;
  customers: {
    number: string;
    name: string;
  }[];
};

export function UserProfile({ api, intl, user, notifier, flipper }: Props) {
  const [userDetails, setUserDetails] = useState<User>();
  const [userSignIns, setUserSignIns] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const loadUserInfos = async () => {
    try {
      const responseUser = await api.get(
        "/users/current?include=userroles,demo_data,customers",
      );
      responseUser.avatar = `https://www.gravatar.com/avatar/${md5(responseUser.email)}?s=160&d=mm`;
      const address = {
        companyName: responseUser.address?.company_name,
        streetName: responseUser.address?.street,
        streetNumber: responseUser.address?.street_number,
        city: responseUser.address?.city,
        zipCode: responseUser.address?.zip_code,
        regionCode: responseUser.address?.region_code,
        countryName: responseUser.address?.country_name,
        countryCode: responseUser.address?.country_code,
        vatNumber: responseUser.address?.vat_number || "",
        billingEmail: responseUser.address?.email,
      };
      const phone = responseUser.address?.phone;
      setUserDetails({ ...responseUser, address, phone });
      const responseSignIns = await api.get(
        `/users/${responseUser.id}/sign_ins`,
        { per_page: 5 },
      );
      setUserSignIns(responseSignIns.sign_ins);
    } catch (error) {
      notifier.showError(api.translateError(error));
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    loadUserInfos();
  }, []);

  const renderAddress = () => {
    let addressItem = (
      <li className="profile-item-line" key={uuid()}>
        -
      </li>
    );
    if (userDetails?.address) {
      const formattedAddress = formatAddress(userDetails.address);
      addressItem =
        formattedAddress.length > 0 ? (
          formatAddress(userDetails.address).map((line: $TSFixMe) => (
            <li className="profile-item-line" key={uuid()}>
              {line}
            </li>
          ))
        ) : (
          <li
            className="profile-item-line"
            data-testid="empty-address-line"
            key={uuid()}
          >
            -
          </li>
        );
    }
    return (
      <>
        <div className="details-item padding-remove">
          <div className="details-item-name">
            {intl.formatMessage({ id: "user_profile.contact_details" })}
          </div>
        </div>
        <ul id="user-profile-address" className="profile-item">
          {addressItem}
        </ul>
      </>
    );
  };

  const renderPhone = (
    <>
      <div className="details-item padding-remove">
        <div className="details-item-name">
          {intl.formatMessage({ id: "label.phone" })}
        </div>
      </div>
      <ul id="user-profile-phone" className="profile-item">
        <li id="phone-number" className="profile-item-line" key={uuid()}>
          {userDetails?.phone || "-"}
        </li>
      </ul>
    </>
  );

  const renderTimeZone = () => {
    const identifier = userDetails?.time_zone;
    const timeZone = getTimeZone(identifier).label;
    return (
      <>
        <div className="details-item padding-remove">
          <div className="details-item-name">
            {intl.formatMessage({ id: "label.time_zone" })}
          </div>
        </div>
        <ul id="user-profile-time-zone" className="profile-item">
          <li id="time-zone" className="profile-item-line" key={uuid()}>
            {timeZone || "-"}
          </li>
        </ul>
      </>
    );
  };

  const renderLocale = () => {
    const identifier = userDetails?.locale;
    const locale = getLocaleOption(identifier)?.label;
    const localeFormat = getLocaleFormat(identifier);
    return (
      <>
        <div className="details-item padding-remove hidden">
          <div className="details-item-name">
            {intl.formatMessage({ id: "label.locale" })}
          </div>
        </div>
        <ul id="user-profile-locale" className="profile-item hidden">
          <li id="locale" className="profile-item-line" key={uuid()}>
            {locale || "-"}
          </li>
          {localeFormat && (
            <li
              id="locale-format"
              className="profile-item-line disabled strong"
              key={uuid()}
            >
              {localeFormat}
            </li>
          )}
        </ul>
      </>
    );
  };

  const renderBillingEmail = (
    <>
      <div className="details-item padding-remove">
        <div className="details-item-name">
          {intl.formatMessage({ id: "label.billing_email" })}
        </div>
      </div>
      <ul id="user-profile-billing-email" className="profile-item">
        <li id="billing-email" className="profile-item-line" key={uuid()}>
          {userDetails?.address.billingEmail || "-"}
        </li>
      </ul>
    </>
  );

  const renderVat = (
    <>
      <div className="details-item padding-remove">
        <div className="details-item-name">
          {intl.formatMessage({ id: "label.vat_number" })}
        </div>
      </div>
      <ul id="user-profile-vat" className="profile-item">
        <li id="phone-number" className="profile-item-line" key={uuid()}>
          {(VatPrefixes as $TSFixMe).forCountryCode(
            userDetails?.address?.countryCode,
          )}
          {userDetails?.address?.vatNumber || "-"}
        </li>
      </ul>
    </>
  );

  const renderUserProfile = (
    <>
      <Details>
        <DetailsPicture>
          <img src={userDetails?.avatar} alt={displayUserName(userDetails)} />
        </DetailsPicture>
        <DetailsHeaderItem
          id="user-profile-name"
          translationKey="label.name"
          value={displayUserName(userDetails, false, true)}
        />
        <DetailsHeaderItem
          id="user-profile-email"
          translationKey="label.email"
          value={userDetails?.email}
        />
      </Details>
      {renderAddress()}
      {renderPhone}
      {renderTimeZone()}
      {renderLocale()}
      {renderBillingEmail}
      {userDetails?.address?.vatNumber && renderVat}
    </>
  );
  const renderDemoDataHandler = flipper?.demoDataActive && <DemoDataHandler />;
  const renderEmailHandler = (
    <>
      <ActionBar>
        <Heading
          id="user-profile-emails"
          level={2}
          title={intl.formatMessage({ id: "user_profile.emails" })}
        />
      </ActionBar>
      <Details>
        {flipper &&
          !isElementHiddenViaFlipper(
            flipper,
            "hideUserProfileNotificationSettings",
          ) && <NotificationEmailsHandler />}
        <NewsletterHandler />
      </Details>
    </>
  );

  const isSsoUser = userDetails?.sso_provider || false;

  const renderContent =
    isLoading || !userDetails ? null : (
      <>
        {renderUserProfile}
        {isSsoUser ? null : (
          <div id="password-edit-form" data-testid="password-edit-form">
            {/* @ts-expect-error TS(2322) FIXME: Type '{ userDetails: undefined; }' is not assignab... Remove this comment to see the full error message */}
            <UserPasswordEdit userDetails={userDetails} />
          </div>
        )}

        <div
          id="multi-factor-authentication-group"
          data-testid="multi-factor-authentication-group"
        >
          {!isSsoUser && <MultiFactorAuthenticationDetails />}
          {isSsoUser && !userDetails?.email?.endsWith("@endress.com") && (
            <MyEndressMfaDetails />
          )}
        </div>
        {renderDemoDataHandler}
        {renderEmailHandler}
        <LoginDetails
          signIns={userSignIns}
          ssoProvider={userDetails?.sso_provider}
        />
        {flipper?.isVerifyActive && (
          <Row className="space-after">
            <Column>
              <div className="user-profile-customers-title">
                <Heading
                  title={intl.formatMessage({
                    id: "user_profile.customers.title",
                  })}
                  level={2}
                />
                <UserProfileCustomersHelp />
              </div>
              {userDetails.customers?.length === 0 && (
                <p>
                  <FormattedMessage id="user_profile.customers.no_results" />
                </p>
              )}
              {userDetails.customers?.length > 0 && (
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage id="user_profile.customers.number" />
                      </th>
                      <th>
                        <FormattedMessage id="user_profile.customers.name" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDetails.customers?.map((customer) => (
                      <tr key={customer.number}>
                        <td>{customer.number}</td>
                        <td>{customer.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Column>
          </Row>
        )}
        {/* @ts-expect-error TS(2322) FIXME: Type '{ exportType: string; user: userShape; }' is... Remove this comment to see the full error message */}
        <DataExports exportType="complete" user={user} />
      </>
    );

  return (
    <Container>
      <ActionBar>
        <Heading
          id="user-profile-header"
          title={intl.formatMessage({ id: "user_profile.header" })}
        />
        <ActionBarButtons>
          <EditActionButton
            id="edit-user-profile-button"
            target="/profile/edit"
          />
        </ActionBarButtons>
      </ActionBar>
      {renderContent}
      <Loader id="user-profile-loader" loading={isLoading} />
    </Container>
  );
}

export default withFlipper(
  withUser(withApi(withNotifier(withIntl(UserProfile)))),
);
